import React, { useState, useMemo, useEffect } from 'react'
import { ArrowPathIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline'
import {
  addTicketsToBasket,
  removeTicketsFromBasket
} from '@lib/spektrix/index'
import useSWR from 'swr'
import SpektrixFetcher from '@lib/spektrix/fetcher'
import { useTicketTypes } from '@lib/use-ticket-types'
import useBasketModel from '@lib/basket-model'
import BasketUpdateEvent from '@lib/basket-update-event'

const MealDeals = ({
  instance,
  mainTicketsInBasket,
  mutate,
  showBeforeBasket = false,
  onMealDealsChange,
  model
}) => {
  const [updatingMealDeals, setUpdatingMealDeals] = useState(false)
  const [selectedMealDeals, setSelectedMealDeals] = useState(0)
  const [pendingRemoval, setPendingRemoval] = useState(false)
  const [initialized, setInitialized] = useState(false)
  // Per-ticket selections: array of ticket type IDs, one per meal deal
  const [perTicketSelections, setPerTicketSelections] = useState([])

  // Get meal deals from instance data
  const mealDeals = instance.meal_deals || []
  // Filter out meal deals with no availability or past stop_selling_at
  const availableMealDeals = mealDeals.filter(md => {
    // Check availability
    if (md.available <= 0) return false

    // Check stop_selling_at
    if (md.stop_selling_at) {
      const stopSellingTime = new Date(md.stop_selling_at)
      if (stopSellingTime <= new Date()) return false
    }

    return true
  })
  const mealDeal = availableMealDeals[0] // Get the first available meal deal

  // All hooks must be called unconditionally and in the same order every render
  const { data: mealDealPlan } = useSWR(
    mealDeal
      ? `/instances/${mealDeal.spektrix_v3_id || mealDeal.id}/plan?$expand=2`
      : null,
    SpektrixFetcher
  )

  const mealDealTicketTypes = useTicketTypes(
    mealDeal ? mealDeal.spektrix_v3_id || mealDeal.id : null
  )

  // Use global basket model
  const basket = useBasketModel()

  // Listen for basket update events
  useEffect(() => {
    const handleBasketUpdate = () => {
      basket.mutate()
    }
    window.addEventListener(BasketUpdateEvent, handleBasketUpdate)
    return () => {
      window.removeEventListener(BasketUpdateEvent, handleBasketUpdate)
    }
  }, [basket])

  // Get ALL meal deal tickets in basket (not filtered by current instance)
  const allMealDealsInBasket = useMemo(() => {
    if (!basket.data?.tickets) return []

    return basket.data.tickets.filter(t => {
      const eventName = t.event?.name || ''
      return (
        eventName.toLowerCase().includes('dinner offer') ||
        eventName.toLowerCase().includes('meal deal')
      )
    })
  }, [basket.data])

  // Get meal deal tickets that match THIS instance's available meal deals
  const instanceMealDealsInBasket = useMemo(() => {
    if (!basket.data?.tickets || availableMealDeals.length === 0) return []

    const mealDealInstanceIds = availableMealDeals.flatMap(md =>
      [
        md.spektrix_v3_id,
        md.id,
        String(md.spektrix_v3_id),
        String(md.id)
      ].filter(Boolean)
    )

    return basket.data.tickets.filter(t => {
      const ticketInstanceId = t.instance?.id || t.instance_id
      return (
        mealDealInstanceIds.includes(ticketInstanceId) ||
        mealDealInstanceIds.includes(String(ticketInstanceId))
      )
    })
  }, [basket.data, availableMealDeals])

  // Calculate values
  const totalMainTickets = mainTicketsInBasket?.length || 0

  // Determine which meal deals to use for display
  // If we have main tickets for this event, show instance-specific meal deals
  // Otherwise, show all meal deals in basket
  const relevantMealDealsInBasket =
    totalMainTickets > 0 ? instanceMealDealsInBasket : allMealDealsInBasket
  const hasMealDealsInBasket = relevantMealDealsInBasket.length > 0

  // In pre-basket mode (reserved seating), track the effective state
  const effectiveMealDealsCount = showBeforeBasket
    ? pendingRemoval
      ? 0
      : selectedMealDeals
    : relevantMealDealsInBasket.length

  const hasMultipleTicketTypes = mealDealTicketTypes.ticketOptions.length > 1

  // Get the default ticket type ID
  const getDefaultTicketTypeId = () => {
    if (mealDealTicketTypes.ticketOptions.length === 0) return null
    const bandDefault = mealDealTicketTypes.ticketOptions.find(
      o => o.ticket?.isBandDefault
    )
    if (bandDefault) return bandDefault.value
    const standardTicket = mealDealTicketTypes.ticketOptions.find(o =>
      o.label.toLowerCase().includes('standard')
    )
    return (standardTicket || mealDealTicketTypes.ticketOptions[0])?.value
  }

  const getTicketTypeById = id =>
    mealDealTicketTypes.ticketOptions.find(o => o.value === id) || null

  // For single-type display and backwards compat
  const getSelectedTicketType = () => {
    if (mealDealTicketTypes.ticketOptions.length === 0) return null
    if (perTicketSelections.length > 0) {
      return (
        getTicketTypeById(perTicketSelections[0]) ||
        getTicketTypeById(getDefaultTicketTypeId())
      )
    }
    return getTicketTypeById(getDefaultTicketTypeId())
  }

  // Build the ticketTypes array from perTicketSelections
  const getTicketTypesArray = () => {
    const defaultId = getDefaultTicketTypeId()
    const count =
      selectedMealDeals ||
      Math.min(totalMainTickets, mealDeal?.available || totalMainTickets)
    return Array.from({ length: count }, (_, i) => {
      const id = perTicketSelections[i] || defaultId
      return getTicketTypeById(id)
    }).filter(Boolean)
  }

  // Sync perTicketSelections array size with the target meal deal count
  useEffect(() => {
    if (mealDealTicketTypes.ticketOptions.length === 0) return
    const defaultId = getDefaultTicketTypeId()
    const targetCount = showBeforeBasket
      ? selectedMealDeals
      : Math.min(totalMainTickets, mealDeal?.available || totalMainTickets)

    if (targetCount > 0 && perTicketSelections.length !== targetCount) {
      setPerTicketSelections(prev => {
        const next = [...prev]

        // When growing, prefer existing basket ticket types over default
        while (next.length < targetCount) {
          const basketTicket = relevantMealDealsInBasket[next.length]
          const basketTypeId = basketTicket?.type
            ? typeof basketTicket.type === 'object'
              ? basketTicket.type.id
              : basketTicket.type
            : null
          // Only use basket type if it's a valid option
          const isValid =
            basketTypeId &&
            mealDealTicketTypes.ticketOptions.some(
              o => o.value === basketTypeId
            )
          next.push(isValid ? basketTypeId : defaultId)
        }

        if (next.length > targetCount) next.length = targetCount
        return next
      })
    } else if (targetCount === 0 && perTicketSelections.length > 0) {
      setPerTicketSelections([])
    }
  }, [
    mealDealTicketTypes.ticketOptions.length,
    selectedMealDeals,
    totalMainTickets,
    mealDeal?.available,
    relevantMealDealsInBasket.length
  ])

  // Initialize selected meal deals from basket when in reserved mode
  useEffect(() => {
    if (
      showBeforeBasket &&
      !initialized &&
      relevantMealDealsInBasket.length > 0
    ) {
      // Initialize to match current main tickets, not basket count
      const initialCount = Math.min(
        totalMainTickets,
        relevantMealDealsInBasket.length
      )
      setSelectedMealDeals(initialCount)

      // If we have more meal deals in basket than main tickets, mark excess for removal
      if (relevantMealDealsInBasket.length > totalMainTickets && model) {
        const excessCount = relevantMealDealsInBasket.length - totalMainTickets
        const mealDealsToRemove = relevantMealDealsInBasket
          .slice(0, excessCount)
          .map(t => t.id)
        model.mealDealsToRemove = mealDealsToRemove
      }

      setInitialized(true)
    }
  }, [
    showBeforeBasket,
    relevantMealDealsInBasket.length,
    initialized,
    totalMainTickets,
    model
  ])

  // Update selected meal deals when main tickets change
  useEffect(() => {
    if (showBeforeBasket && selectedMealDeals > 0) {
      // Only update if user has already opted in to meal deals
      const expectedCount = Math.min(
        totalMainTickets,
        mealDeal?.available || totalMainTickets
      )

      // Update count if tickets were removed
      if (expectedCount < selectedMealDeals && !pendingRemoval) {
        setSelectedMealDeals(expectedCount)

        // Update parent when count changes due to ticket removal
        if (
          mealDeal &&
          mealDealPlan &&
          mealDealTicketTypes.ticketOptions.length > 0
        ) {
          const ticketType = getSelectedTicketType()
          if (ticketType && onMealDealsChange) {
            if (expectedCount === 0) {
              onMealDealsChange(null)
            } else {
              const newMealDealsToAdd = Math.max(
                0,
                expectedCount - relevantMealDealsInBasket.length
              )
              onMealDealsChange({
                count: newMealDealsToAdd,
                mealDeal,
                mealDealPlan: {
                  ...mealDealPlan,
                  seatingPlanId:
                    mealDealPlan.spektrix_v3_id ||
                    mealDealPlan.spektrix_id ||
                    mealDealPlan.id
                },
                ticketType
              })
            }
          }
        }
      }

      // Always check if we need to mark meal deals for removal
      if (relevantMealDealsInBasket.length > totalMainTickets && model) {
        const excessCount = relevantMealDealsInBasket.length - totalMainTickets
        const newMealDealsToRemove = relevantMealDealsInBasket
          .slice(0, excessCount)
          .map(t => t.id)
        // Only update if different to avoid loops
        if (
          JSON.stringify(model.mealDealsToRemove) !==
          JSON.stringify(newMealDealsToRemove)
        ) {
          model.mealDealsToRemove = newMealDealsToRemove
        }
      } else if (
        relevantMealDealsInBasket.length <= totalMainTickets &&
        model &&
        model.mealDealsToRemove
      ) {
        // Clear removal if we don't have excess
        model.mealDealsToRemove = null
      }
    }
  }, [
    showBeforeBasket,
    totalMainTickets,
    mealDeal?.available,
    relevantMealDealsInBasket.length,
    model,
    pendingRemoval,
    mealDeal,
    mealDealPlan,
    mealDealTicketTypes.ticketOptions.length,
    onMealDealsChange
  ])

  // Auto-adjust meal deals based on model suggestion
  useEffect(() => {
    if (showBeforeBasket && model?.suggestMealDealAdjustment && mealDeal) {
      const suggestion = model.suggestMealDealAdjustment

      // Calculate how many meal deals we should have selected
      // This is the existing meal deals PLUS the suggested additional ones
      const targetCount = Math.min(
        suggestion.currentMealDeals + suggestion.suggestedToAdd,
        mealDeal.available || suggestion.currentTickets
      )

      // Only auto-select if we haven't already set this count and not pending removal
      if (selectedMealDeals !== targetCount && !pendingRemoval) {
        setSelectedMealDeals(targetCount)
      }

      // Always clear the suggestion to prevent loops
      model.suggestMealDealAdjustment = null
    }
  }, [showBeforeBasket, model?.suggestMealDealAdjustment])

  // Notify parent of meal deal changes when in showBeforeBasket mode
  useEffect(() => {
    if (!showBeforeBasket) return

    // Handle pending removal
    if (pendingRemoval && relevantMealDealsInBasket.length > 0) {
      // Set meal deals to remove on the model
      if (model) {
        const newMealDealsToRemove = relevantMealDealsInBasket.map(t => t.id)
        // Only update if different
        if (
          JSON.stringify(model.mealDealsToRemove) !==
          JSON.stringify(newMealDealsToRemove)
        ) {
          model.mealDealsToRemove = newMealDealsToRemove
        }
      }
      if (onMealDealsChange) {
        onMealDealsChange(null)
      }
    }
    // Handle meal deal selection
    else if (
      selectedMealDeals > 0 &&
      mealDeal &&
      mealDealPlan &&
      mealDealTicketTypes.ticketOptions.length > 0
    ) {
      // Don't clear removal flag if we still have excess meal deals
      if (
        model &&
        relevantMealDealsInBasket.length <= selectedMealDeals &&
        model.mealDealsToRemove
      ) {
        model.mealDealsToRemove = null
      }

      const ticketType = getSelectedTicketType()
      const ticketTypes = getTicketTypesArray()
      if (ticketType && onMealDealsChange) {
        // Calculate how many NEW meal deals to add (not total)
        const newMealDealsToAdd = Math.max(
          0,
          selectedMealDeals - relevantMealDealsInBasket.length
        )

        onMealDealsChange({
          count: newMealDealsToAdd, // Only the additional ones to add
          mealDeal,
          mealDealPlan: {
            ...mealDealPlan,
            seatingPlanId:
              mealDealPlan.spektrix_v3_id ||
              mealDealPlan.spektrix_id ||
              mealDealPlan.id
          },
          ticketType,
          ticketTypes: ticketTypes.slice(
            relevantMealDealsInBasket.length
          )
        })
      }
    } else {
      // Clear both
      if (model && model.mealDealsToRemove) {
        model.mealDealsToRemove = null
      }
      if (onMealDealsChange) {
        onMealDealsChange(null)
      }
    }
  }, [
    selectedMealDeals,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(perTicketSelections),
    pendingRemoval,
    showBeforeBasket,
    mealDeal,
    mealDealPlan,
    mealDealTicketTypes.ticketOptions.length,
    relevantMealDealsInBasket.length,
    onMealDealsChange,
    model
  ])

  // Define handler AFTER all hooks
  const handleToggleMealDeals = async () => {
    // In pre-basket mode (reserved seating), handle staging changes
    if (showBeforeBasket) {
      if (relevantMealDealsInBasket.length > 0) {
        // Toggle pending removal
        setPendingRemoval(!pendingRemoval)
        if (!pendingRemoval) {
          // If marking for removal, clear any selected count
          setSelectedMealDeals(0)
          onMealDealsChange(null)
        } else {
          // If unmarking removal, restore the meal deals
          setSelectedMealDeals(relevantMealDealsInBasket.length)
          // Don't update parent here - wait for them to select ticket type
        }
      } else if (selectedMealDeals > 0) {
        // Remove selection
        setSelectedMealDeals(0)
        onMealDealsChange(null)
      } else {
        // Add selection
        const toSelect = Math.min(totalMainTickets, mealDeal.available || 0)
        setSelectedMealDeals(toSelect)
        // Don't update parent here - wait for ticket types to load
      }
      return
    }

    setUpdatingMealDeals(true)

    try {
      if (hasMealDealsInBasket) {
        // Remove relevant meal deal tickets from basket
        const ticketIds = relevantMealDealsInBasket.map(t => t.id)

        if (!ticketIds.length || ticketIds.some(id => !id)) {
          throw new Error('Cannot remove tickets - invalid ticket IDs')
        }

        await removeTicketsFromBasket(ticketIds)
      } else {
        // Add meal deals matching main ticket quantity
        const ticketTypes = getTicketTypesArray()

        if (ticketTypes.length > 0 && mealDealPlan) {
          const toAdd = Math.min(totalMainTickets, mealDeal.available || 0)

          if (toAdd > 0) {
            const ticketsToAdd = []
            for (let i = 0; i < toAdd; i++) {
              const tt = ticketTypes[i] || ticketTypes[0]
              ticketsToAdd.push({
                instance: mealDeal.spektrix_v3_id || mealDeal.id,
                seatingPlan:
                  mealDealPlan.spektrix_v3_id ||
                  mealDealPlan.spektrix_id ||
                  mealDealPlan.id,
                type: tt.value
              })
            }

            await addTicketsToBasket(ticketsToAdd)
          }
        }
      }

      // Refresh basket
      if (mutate) mutate()
      if (basket.mutate) basket.mutate()

      // Force a re-fetch after a short delay to ensure the server has updated
      setTimeout(() => {
        if (basket.mutate) basket.mutate()
        setUpdatingMealDeals(false)
      }, 1000)
    } catch (error) {
      alert(`Error updating meal deals: ${error.message || 'Unknown error'}`)
      setUpdatingMealDeals(false)
    }
  }

  const renderToggleButton = () => (
    <button
      onClick={handleToggleMealDeals}
      disabled={updatingMealDeals}
      className={`
        flex items-center space-x-2 px-4 py-2 rounded-lg font-medium shrink-0
        transition-colors disabled:opacity-50 disabled:cursor-not-allowed
        ${
          (showBeforeBasket && (selectedMealDeals > 0 || pendingRemoval)) ||
          (!showBeforeBasket && relevantMealDealsInBasket.length > 0)
            ? pendingRemoval
              ? 'bg-amber-300 text-amber-700 hover:bg-amber-400 dark:bg-amber-600 dark:text-amber-300 dark:hover:bg-amber-500'
              : 'bg-gray-300 text-gray-700 hover:bg-gray-400 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500'
            : 'bg-green-600 text-white hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600'
        }
      `}
    >
      {updatingMealDeals ? (
        <>
          <ArrowPathIcon className='w-5 h-5 animate-spin' />
          <span>Updating...</span>
        </>
      ) : (showBeforeBasket && (selectedMealDeals > 0 || pendingRemoval)) ||
        (!showBeforeBasket && relevantMealDealsInBasket.length > 0) ? (
        <>
          <XMarkIcon className='w-5 h-5' />
          <span>
            {showBeforeBasket && pendingRemoval
              ? 'Undo Remove'
              : showBeforeBasket && selectedMealDeals > 0
              ? `Remove ${selectedMealDeals}`
              : `Remove ${relevantMealDealsInBasket.length}`}
          </span>
        </>
      ) : (
        <>
          <PlusIcon className='w-5 h-5' />
          <span>
            Add{' '}
            {Math.min(
              totalMainTickets,
              mealDeal.available || totalMainTickets
            )}
          </span>
        </>
      )}
    </button>
  )

  // NOW we can do conditional returns - AFTER all hooks have been called

  // Don't show if no meal deals available or none have availability
  if (availableMealDeals.length === 0) {
    return null
  }

  // If showing before basket and no tickets selected yet, show disabled state
  if (showBeforeBasket && totalMainTickets === 0) {
    return (
      <div className='mt-6 p-4 bg-gray-100 dark:bg-gray-700 rounded-lg opacity-60'>
        <p className='text-sm text-gray-600 dark:text-gray-400'>
          Select ticket types above to add meal deals
        </p>
      </div>
    )
  }

  // If no tickets for current event and no meal deals in basket at all, don't show anything
  if (!showBeforeBasket && totalMainTickets === 0 && !hasMealDealsInBasket) {
    return null
  }

  if (!mealDeal) return null

  // Show loading state while fetching meal deal data
  if (!mealDealPlan || mealDealTicketTypes.isLoading) {
    return (
      <div className='mt-6 p-4 bg-gray-100 dark:bg-gray-700 rounded-lg'>
        <div className='flex items-center justify-center space-x-2 text-gray-600 dark:text-gray-400'>
          <ArrowPathIcon className='w-5 h-5 animate-spin' />
          <span>Loading meal deal options...</span>
        </div>
      </div>
    )
  }

  // Don't show if no ticket types available
  if (mealDealTicketTypes.ticketOptions.length === 0) {
    return null
  }

  return (
    <div className='mt-6 p-4 bg-gradient-to-r from-green-50 to-gray-50 dark:from-gray-700 dark:to-gray-800 rounded-lg border-2 border-green-200 dark:border-green-700'>
      <div className='flex items-center justify-between'>
        <div className='flex-1'>
          <div className='flex items-center space-x-2'>
            <h3 className='font-semibold text-gray-900 dark:text-white'>
              Meal Deal
            </h3>
            <span className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'>
              SAVE
            </span>
          </div>
          <p className='text-sm text-gray-600 dark:text-gray-400 mt-1'>
            {mealDeal.event_name}
          </p>
          {mealDealTicketTypes.ticketOptions.length > 0 &&
            !hasMultipleTicketTypes && (
            <div className='mt-2'>
              <p className='text-2xl font-bold text-gray-900 dark:text-white'>
                {getSelectedTicketType()?.label.split(' - ')[1] ||
                  getSelectedTicketType()?.label}
              </p>
              <p className='text-sm text-gray-600 dark:text-gray-400'>
                {getSelectedTicketType()?.label.split(' - ')[0]}
              </p>
            </div>
          )}
          {(hasMealDealsInBasket ||
            (showBeforeBasket && selectedMealDeals > 0)) && (
            <div className='mt-2 flex items-center space-x-2'>
              <span
                className={
                  pendingRemoval
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-green-600 dark:text-green-400'
                }
              >
                <svg
                  className='w-5 h-5'
                  fill='currentColor'
                  viewBox='0 0 20 20'
                >
                  {pendingRemoval ? (
                    <path
                      fillRule='evenodd'
                      d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
                      clipRule='evenodd'
                    />
                  ) : (
                    <path
                      fillRule='evenodd'
                      d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                      clipRule='evenodd'
                    />
                  )}
                </svg>
              </span>
              <p
                className={`text-sm font-medium ${
                  pendingRemoval
                    ? 'text-red-700 dark:text-red-400 line-through'
                    : 'text-green-700 dark:text-green-400'
                }`}
              >
                {effectiveMealDealsCount} meal deal
                {effectiveMealDealsCount !== 1 ? 's' : ''}
                {showBeforeBasket &&
                !pendingRemoval &&
                selectedMealDeals === 0 &&
                relevantMealDealsInBasket.length > 0
                  ? ' in basket'
                  : showBeforeBasket &&
                    selectedMealDeals > 0 &&
                    relevantMealDealsInBasket.length === 0
                  ? ' to be added'
                  : showBeforeBasket && pendingRemoval
                  ? ' to be removed'
                  : ' in basket'}
                {effectiveMealDealsCount > 0 && !pendingRemoval
                  ? (() => {
                      const types = getTicketTypesArray()
                      const total = types.reduce(
                        (sum, tt) => sum + (tt?.ticket?.amount || 0),
                        0
                      )
                      return total > 0 ? ` - Total: £${total.toFixed(2)}` : ''
                    })()
                  : ''}
              </p>
            </div>
          )}
        </div>

        {!hasMultipleTicketTypes && renderToggleButton()}
      </div>

      {hasMultipleTicketTypes && perTicketSelections.length > 0 && (
        <div className='mt-3'>
          <p className='text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
            Choose your options:
          </p>
          <div className='flex flex-col gap-2'>
            {perTicketSelections.map((typeId, idx) => (
              <div key={idx} className='flex flex-col sm:flex-row sm:items-center gap-2'>
                <span className='text-xs font-medium text-gray-500 dark:text-gray-400 shrink-0 sm:w-16'>
                  Ticket {idx + 1}
                </span>
                <select
                  value={typeId || ''}
                  onChange={e => {
                    setPerTicketSelections(prev => {
                      const next = [...prev]
                      next[idx] = e.target.value
                      return next
                    })
                  }}
                  className='flex-1 min-w-0 px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 appearance-none bg-no-repeat bg-[length:16px_16px] bg-[position:right_12px_center] bg-[url("data:image/svg+xml,%3Csvg%20xmlns%3D%27http%3A//www.w3.org/2000/svg%27%20viewBox%3D%270%200%2020%2020%27%20fill%3D%27%236b7280%27%3E%3Cpath%20fill-rule%3D%27evenodd%27%20d%3D%27M5.23%207.21a.75.75%200%20011.06.02L10%2011.168l3.71-3.938a.75.75%200%20111.08%201.04l-4.25%204.5a.75.75%200%2001-1.08%200l-4.25-4.5a.75.75%200%2001.02-1.06z%27%20clip-rule%3D%27evenodd%27/%3E%3C/svg%3E")] pr-10'
                >
                  {mealDealTicketTypes.ticketOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>
      )}

      {hasMultipleTicketTypes && (
        <div className='mt-3'>
          {renderToggleButton()}
        </div>
      )}

      {!hasMealDealsInBasket && totalMainTickets > 0 && (
        <div className='mt-3 pt-3 border-t border-gray-200 dark:border-gray-600'>
          <p className='text-sm text-gray-600 dark:text-gray-400 font-medium'>
            {mealDeal.available < totalMainTickets ? (
              <>
                ⚠️ Limited availability - only {mealDeal.available} meal deal
                {mealDeal.available !== 1 ? 's' : ''} remaining
              </>
            ) : (
              <>
                💡 Save money by adding{' '}
                {Math.min(
                  totalMainTickets,
                  mealDeal.available || totalMainTickets
                )}{' '}
                meal deal
                {Math.min(
                  totalMainTickets,
                  mealDeal.available || totalMainTickets
                ) > 1
                  ? 's'
                  : ''}{' '}
                to match your ticket{totalMainTickets > 1 ? 's' : ''}
              </>
            )}
          </p>
        </div>
      )}
    </div>
  )
}

export default MealDeals
